/** ワークフローの一覧・パネル・詳細で共通に使うセルと札。 */
import { Space, Tag } from "antd";
import { Link } from "react-router-dom";

import type { TaskLoan, WfExternalRequest, WfRequest, WfTask } from "../../lib/api";
import { StatusTag } from "../../ui";
import {
  externalStatusTone,
  kindLabel,
  requestStatusLabel,
  requestStatusTone,
  statusLabel,
  taskStatusLabel,
  taskStatusTone,
} from "./constants";
import { AutoStateTag, SlaTag } from "./wfActions";

export function requestStatusTag(r: Pick<WfRequest, "status">) {
  return <StatusTag tone={requestStatusTone(r.status)}>{requestStatusLabel(r.status)}</StatusTag>;
}

export function externalStatusTag(e: Pick<WfExternalRequest, "status" | "status_label" | "send_unknown">) {
  return (
    <Space size={4} wrap>
      <StatusTag tone={externalStatusTone(e.status)}>{e.status_label}</StatusTag>
      {e.send_unknown && <Tag color="orange">送信結果不明</Tag>}
    </Space>
  );
}

/** 状態 + 自動実行の進み具合 + SLA。表の 1 列に収める(タスク表は 7 列まで)。 */
export function taskStatusCell(t: WfTask) {
  return (
    <Space size={4} wrap>
      <StatusTag tone={taskStatusTone(t.status)}>{t.status_label || taskStatusLabel(t.status)}</StatusTag>
      <AutoStateTag task={t} />
      <SlaTag sla={t.sla} />
    </Space>
  );
}

/** 「入場/PC」のような工程名 + 回収・貸与・操作の札。 */
export function taskNameCell(t: WfTask, onOpen?: () => void) {
  const name = `${t.section ? `${t.section}/` : ""}${t.name}`;
  return (
    <Space size={4} wrap>
      {onOpen ? (
        // 行クリックと併存。キーボード(Tab → Enter)でも右パネルを開けるように(fix1 中5)
        <button type="button" className="ui-link-btn" onClick={onOpen}>
          {name}
        </button>
      ) : (
        <span>{name}</span>
      )}
      {t.action === "deprovision" && <Tag color="volcano">{t.action_label}</Tag>}
      {t.loan && <Tag color="blue">貸与</Tag>}
      {t.loan_id && <Tag>台帳 #{t.loan_id}</Tag>}
      {t.op_label && <Tag color="purple">{t.op_label}</Tag>}
    </Space>
  );
}

/** 貸与品 1 行の短い表記: 「PC SM-PC-0002(レンタル)」。個体が無ければ品目だけ。 */
export function taskLoanText(l: TaskLoan): string {
  const asset = l.asset_no ? ` ${l.asset_no}` : "";
  const own = l.ownership_label ? `(${l.ownership_label})` : "";
  return `${l.item}${asset}${own}`;
}

/**
 * 回収タスクの下に出す貸与品の 1 行。回収タスクでなければ何も出さない。
 * 完了後は「このタスクで返した」行なので見出しを変える。
 */
export function taskLoansCell(t: WfTask) {
  if (t.loans == null) return null;
  const head = t.status === "done" ? "返却した貸与品" : "貸与品";
  if (t.loans.length === 0) {
    return (
      <div className="ui-cell-sub" data-testid="task-loans">
        {head}: なし
      </div>
    );
  }
  return (
    <div className="ui-cell-sub" data-testid="task-loans">
      {head}: {t.loans.map(taskLoanText).join("、")}
    </div>
  );
}

/** 申請へのリンク(#12 入社)。申請に紐づかないタスクは割当種別を出す。 */
export function requestLink(requestId: number | null, kind: string, fallback = "") {
  if (!requestId) return fallback ? <span className="ui-cell-unset">{fallback}</span> : null;
  return (
    <Link to={`/hr/wf-requests/${requestId}`}>
      #{requestId} {kindLabel(kind)}
    </Link>
  );
}

export function employeeLink(id: number | null | undefined, name: string, status?: string) {
  if (!id) return name || null;
  return (
    <>
      <Link to={`/hr/employees/${id}`}>{name || `#${id}`}</Link>
      {status && <Tag style={{ marginLeft: 6 }}>{statusLabel(status)}</Tag>}
    </>
  );
}

/** 検索語(空白区切り AND)が、行の文字列のどこかに含まれるか。サーバーに検索が無い一覧の手元絞り込み。 */
export function matchesAll(q: string, ...fields: (string | number | null | undefined)[]): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fields.map((f) => (f == null ? "" : String(f))).join(" ").toLowerCase();
  return terms.every((t) => hay.includes(t));
}
